/**
 * useSocketConnection - owns the WebSocket connection lifecycle and ALL
 * incoming server message handling. Mount exactly ONCE (in App).
 *
 * Components send actions through the plain `socketActions` module instead.
 */

import { useEffect } from 'react';
import type { ServerEvent, ClaimedWord } from '../types/generated';
import {
  connectSocket,
  isSocketConnected,
  measureLatency,
  onMessage,
  onConnect,
  onDisconnect,
  onReconnectAttempt,
  onReconnect,
  onReconnectFailed,
  sendMessage,
} from '../lib/socket';
import {
  claimTiming,
  clearClaimTimers,
  clearExplicitLookups,
  consumeExplicitLookup,
  resetClaimStatus,
  scheduleClaimIdle,
} from '../lib/socketActions';
import { recordClaimSnapshot } from '../lib/claimSnapshots';
import { useGameStore } from '../stores/gameStore';
import { triggerHaptic } from '../utils/haptics';
import { calculateScore } from '../utils/game';

// ============================================================================
// Module-level connection state (the hook is mounted once)
// ============================================================================

const PING_INTERVAL_MS = 5000;
const REJOIN_TIMEOUT_MS = 4000;
const MAX_REJOIN_ATTEMPTS = 3;

let pingInterval: number | null = null;
let rejoinTimer: number | null = null;
let hasRejoined = false;
let rejoinAttempts = 0;

function startPing(): void {
  stopPing();
  const sample = () => measureLatency((ms) => useGameStore.getState().setLatency(ms));
  sample();
  pingInterval = window.setInterval(sample, PING_INTERVAL_MS);
}

function stopPing(): void {
  if (pingInterval !== null) {
    window.clearInterval(pingInterval);
    pingInterval = null;
  }
}

function clearRejoinTimer(): void {
  if (rejoinTimer !== null) {
    window.clearTimeout(rejoinTimer);
    rejoinTimer = null;
  }
}

function resetRejoinCounters(): void {
  clearRejoinTimer();
  hasRejoined = false;
  rejoinAttempts = 0;
}

/** Rejoin failed for good - drop back to the lobby with an explanation */
function failRejoin(): void {
  if (import.meta.env.DEV) {
    console.log('[Socket] Rejoin failed, resetting state');
  }
  resetRejoinCounters();
  clearClaimTimers();
  clearExplicitLookups();
  const store = useGameStore.getState();
  store.reset();
  // reset() clears toasts, so add the explanation afterwards
  store.addToast({ type: 'error', message: 'Couldn’t rejoin — the game is no longer available' });
}

function attemptRejoin(): void {
  const { roomCode, playerId, rejoinToken } = useGameStore.getState();
  if (!roomCode || !playerId || hasRejoined) return;
  if (!rejoinToken) {
    failRejoin();
    return;
  }

  if (rejoinAttempts >= MAX_REJOIN_ATTEMPTS) {
    failRejoin();
    return;
  }

  rejoinAttempts++;
  if (import.meta.env.DEV) {
    console.log('[Socket] Auto-rejoining room', roomCode, 'as player', playerId, `(attempt ${rejoinAttempts} of ${MAX_REJOIN_ATTEMPTS})`);
  }
  if (!sendMessage({ type: 'REJOIN_ROOM', roomCode, playerId, rejoinToken })) return;

  clearRejoinTimer();
  rejoinTimer = window.setTimeout(() => {
    rejoinTimer = null;
    // Only fail if we're still connected and never heard back
    if (!hasRejoined && isSocketConnected()) {
      failRejoin();
    }
  }, REJOIN_TIMEOUT_MS);
}

function handleConnected(): void {
  const store = useGameStore.getState();
  store.setConnected(true);
  store.setReconnecting(false);
  attemptRejoin();
  startPing();
}

// ============================================================================
// Incoming message handler
// ============================================================================

/**
 * Capture stolen words' letters + on-screen positions BEFORE any store update
 * (and before lastEvent changes) so the claim animation can start from them.
 */
function captureClaimSnapshot(event: Extract<ServerEvent, { type: 'WORD_CLAIMED' }>): void {
  const gs = useGameStore.getState().gameState;
  const stolen = (event.stolenFromPlayers ?? []).map((s) => {
    const word = gs?.playerWords[s.playerId]?.find((w) => w.id === s.wordId);
    return { playerId: s.playerId, wordId: s.wordId, letters: word?.letters ?? [] };
  });
  recordClaimSnapshot(event.claimedWord.id, stolen);
}

function handleMessage(event: ServerEvent): void {
  if (event.type === 'WORD_CLAIMED') {
    captureClaimSnapshot(event);
  }

  const store = useGameStore.getState();
  store.setLastEvent(event);

  switch (event.type) {
    case 'ROOM_CREATED':
      resetRejoinCounters();
      store.setPlayerId(event.playerId);
      store.setRoomCode(event.roomCode);
      store.setRejoinToken(event.rejoinToken);
      store.setGameState(event.state);
      store.setCreatingRoom(false);
      break;

    case 'ROOM_JOINED':
      resetRejoinCounters();
      store.setPlayerId(event.playerId);
      store.setRoomCode(event.state.roomCode);
      store.setRejoinToken(event.rejoinToken);
      store.setGameState(event.state);
      store.setJoiningRoom(false);
      break;

    case 'ROOM_REJOINED':
      // Successfully rejoined after reconnection - only place counters reset
      clearRejoinTimer();
      hasRejoined = true;
      rejoinAttempts = 0;
      resetClaimStatus();
      store.setFlippingTile(false);
      store.setPlayerId(event.playerId);
      store.setRoomCode(event.state.roomCode);
      store.setRejoinToken(event.rejoinToken);
      store.setGameState(event.state);
      break;

    case 'PLAYER_JOINED':
      // Update game state with new player (idempotent - skip if already exists)
      useGameStore.setState((state) => {
        if (!state.gameState) return state;
        if (state.gameState.players.some((p) => p.id === event.player.id)) {
          return state;
        }
        return {
          gameState: {
            ...state.gameState,
            players: [...state.gameState.players, event.player],
          },
        };
      });
      break;

    case 'PLAYER_LEFT': {
      // Remove player from game state and clean up all their data
      const leavingPlayerId = event.playerId;
      useGameStore.setState((state) => {
        if (!state.gameState) return state;

        const { [leavingPlayerId]: removedWords, ...remainingPlayerWords } = state.gameState.playerWords;
        const { [leavingPlayerId]: _removedScore, ...remainingScores } = state.gameState.scores;

        const newTurnOrder = state.gameState.turnOrder.filter((id) => id !== leavingPlayerId);

        // Adjust current turn index if needed
        let newTurnIndex = state.gameState.currentTurnIndex;
        if (newTurnOrder.length > 0) {
          const leavingTurnIndex = state.gameState.turnOrder.indexOf(leavingPlayerId);
          if (leavingTurnIndex !== -1 && leavingTurnIndex < state.gameState.currentTurnIndex) {
            newTurnIndex = Math.max(0, newTurnIndex - 1);
          }
          if (newTurnIndex >= newTurnOrder.length) {
            newTurnIndex = 0;
          }
        }

        // Clear any selected words that belonged to the leaving player
        const leavingPlayerWordIds = new Set((removedWords || []).map((w) => w.id));
        const newSelectedWords = new Set(
          [...state.selectedWordsToSteal].filter((id) => !leavingPlayerWordIds.has(id))
        );

        const updatedPlayers = state.gameState.players
          .filter((p) => p.id !== leavingPlayerId)
          .map((p) => {
            if (!event.newHostId) return p;
            const isHost = p.id === event.newHostId;
            return p.isHost === isHost ? p : { ...p, isHost };
          });

        return {
          gameState: {
            ...state.gameState,
            players: updatedPlayers,
            playerWords: remainingPlayerWords,
            scores: remainingScores,
            turnOrder: newTurnOrder,
            currentTurnIndex: newTurnIndex,
          },
          selectedWordsToSteal: newSelectedWords,
        };
      });
      break;
    }

    case 'GAME_STARTED':
      store.setStartingGame(false);
      useGameStore.setState((state) => {
        if (!state.gameState) return state;
        return {
          gameState: {
            ...state.gameState,
            status: 'playing',
            turnOrder: event.turnOrder,
            currentTurnIndex: 0,
            turnStartedAt: event.turnStartedAt ?? null,
            turnTimerMs: event.turnTimerMs ?? state.gameState.turnTimerMs,
            endTimerMs: event.endTimerMs ?? state.gameState.endTimerMs,
            isPaused: false,
            pausedPlayerIds: [],
          },
          lastFlip: null,
          finalResults: null,
          endGameTimer: null,
          resumeCountdownEndsAt: null,
        };
      });
      break;

    case 'TILE_FLIPPED':
      store.setFlippingTile(false);
      useGameStore.setState((state) => {
        if (!state.gameState) return state;
        const nextTurnIndex = state.gameState.turnOrder.indexOf(event.nextTurnPlayerId);
        const middle = [...state.gameState.middle, event.letter];
        return {
          gameState: {
            ...state.gameState,
            middle,
            vowelsRemaining: event.vowelsRemaining,
            consonantsRemaining: event.consonantsRemaining,
            currentTurnIndex: nextTurnIndex >= 0 ? nextTurnIndex : 0,
            turnStartedAt: event.turnStartedAt ?? null,
          },
          lastFlip: { letter: event.letter, middleLength: middle.length },
        };
      });
      break;

    case 'WORD_CLAIMED': {
      const isMine = event.playerId === store.playerId;
      const gs = store.gameState;

      // Idempotency check: skip if we've already applied this claim
      if (gs && (gs.playerWords[event.playerId] || []).some((w) => w.id === event.claimedWord.id)) {
        break;
      }

      if (isMine) {
        if (import.meta.env.DEV && claimTiming.startTime > 0) {
          const roundTripMs = Math.round(performance.now() - claimTiming.startTime);
          console.log(`[Claim] ${event.claimedWord.word} success - round trip: ${roundTripMs}ms`);
          claimTiming.startTime = 0;
        }
        clearClaimTimers();
        store.setClaimStatus('success');
        triggerHaptic('success');
        scheduleClaimIdle(1000);
      }

      // Did another player steal a word we had selected?
      const stolenWordIds = new Set(event.stolenFromPlayers?.map((s) => s.wordId) || []);
      const ourSelectionWasStolen =
        !isMine && [...store.selectedWordsToSteal].some((id) => stolenWordIds.has(id));

      useGameStore.setState((state) => {
        if (!state.gameState) return state;

        // Keep untouched players' word arrays by reference (lets memoized
        // PlayerArea components skip re-rendering)
        const newPlayerWords: Record<string, ClaimedWord[]> = { ...state.gameState.playerWords };
        for (const s of event.stolenFromPlayers || []) {
          const words = newPlayerWords[s.playerId];
          if (words) {
            newPlayerWords[s.playerId] = words.filter((w) => w.id !== s.wordId);
          }
        }
        newPlayerWords[event.playerId] = [
          ...(newPlayerWords[event.playerId] || []),
          event.claimedWord,
        ];

        const newSelectedWords = isMine
          ? new Set<string>()
          : stolenWordIds.size > 0
          ? new Set([...state.selectedWordsToSteal].filter((id) => !stolenWordIds.has(id)))
          : state.selectedWordsToSteal;

        return {
          gameState: {
            ...state.gameState,
            middle: event.newMiddle,
            playerWords: newPlayerWords,
            scores: { ...state.gameState.scores, ...event.updatedScores, [event.playerId]: event.newScore },
          },
          selectedWordsToSteal: newSelectedWords,
        };
      });

      if (ourSelectionWasStolen) {
        store.addToast({ type: 'info', message: 'Word you selected was just stolen!' });
      }
      break;
    }

    case 'CLAIM_REJECTED':
      if (import.meta.env.DEV && claimTiming.startTime > 0) {
        const roundTripMs = Math.round(performance.now() - claimTiming.startTime);
        console.log(`[Claim] ${claimTiming.word} rejected - round trip: ${roundTripMs}ms, reason: ${event.reason}`);
        claimTiming.startTime = 0;
      }
      clearClaimTimers();
      store.setClaimStatus('error', event.reason);
      triggerHaptic('error');
      scheduleClaimIdle(2000);
      break;

    case 'GAME_ENDED':
      resetClaimStatus();
      useGameStore.setState((state) => ({
        // Freeze results now so later events can't change the displayed winner
        finalResults: {
          finalScores: event.finalScores,
          winner: event.winner ?? event.finalScores[0] ?? null,
        },
        gameState: state.gameState ? { ...state.gameState, status: 'ended' } : state.gameState,
        endGameTimer: null,
        resumeCountdownEndsAt: null,
        selectedWordsToSteal: new Set<string>(),
      }));
      break;

    case 'CHAT_MESSAGE': {
      const chatPlayer = store.gameState?.players.find((p) => p.id === event.playerId);
      store.addChatMessage({
        id: `${event.playerId}-${event.timestamp}`,
        playerId: event.playerId,
        playerName: event.playerName || chatPlayer?.name || 'Unknown',
        colorIndex: chatPlayer?.colorIndex,
        text: event.message,
        timestamp: event.timestamp,
      });
      break;
    }

    case 'END_TIMER_STARTED':
      store.setEndGameTimer(event.endsAt);
      break;

    case 'LOOKUP_RESULT': {
      const word = event.word.toUpperCase();

      // Real-time validation feedback for the claim input
      if (store.wordValidation?.word === word) {
        store.setWordValidation({ word, isValid: event.isValid });
      }

      // Only post to chat for lookups the user explicitly asked for
      if (consumeExplicitLookup(word)) {
        const points = calculateScore(word.length);
        store.addChatMessage({
          id: `lookup-${Date.now()}-${word}`,
          playerId: 'system',
          playerName: 'Dictionary',
          text: event.isValid ? `"${word}" is valid (${points} pts)` : `"${word}" is not valid`,
          timestamp: Date.now(),
          isSystem: true,
          isLookup: true,
          isValid: event.isValid,
        });
      }
      break;
    }

    case 'REMATCH_STARTED':
      resetClaimStatus();
      useGameStore.setState({ lastFlip: null, resumeCountdownEndsAt: null });
      store.setGameState(event.state);
      store.addToast({ type: 'success', message: 'Rematch started!' });
      break;

    case 'AI_DIFFICULTY_UPDATED':
      useGameStore.setState((state) => {
        if (!state.gameState) return state;
        return {
          gameState: {
            ...state.gameState,
            players: state.gameState.players.map((p) =>
              p.id === event.playerId ? { ...p, aiDifficulty: event.difficulty } : p
            ),
          },
        };
      });
      break;

    case 'GAME_SETTINGS_UPDATED':
      useGameStore.setState((state) => {
        if (!state.gameState) return state;
        return {
          gameState: {
            ...state.gameState,
            turnTimerMs: event.turnTimerMs,
            endTimerMs: event.endTimerMs,
          },
        };
      });
      break;

    case 'GAME_STATE':
      // Full state sync (REQUEST_SYNC, or broadcast after rejoin/disconnect).
      // setGameState only replaces server-owned state.
      store.setGameState(event.state);
      if (event.state.status !== 'playing') {
        store.setResumeCountdownEndsAt(null);
      }
      break;

    case 'PLAYER_PAUSED':
      useGameStore.setState((state) => {
        if (!state.gameState) return state;
        return {
          gameState: { ...state.gameState, pausedPlayerIds: event.pausedPlayerIds },
        };
      });
      break;

    case 'GAME_PAUSED':
      useGameStore.setState((state) => {
        if (!state.gameState) return state;
        return {
          gameState: { ...state.gameState, isPaused: true, pausedPlayerIds: event.pausedPlayerIds },
          resumeCountdownEndsAt: null,
        };
      });
      break;

    case 'GAME_RESUMED':
      // Game is no longer paused - show countdown before actual resume
      store.setResumeCountdownEndsAt(event.resumeAt);
      useGameStore.setState((state) => {
        if (!state.gameState) return state;
        return {
          gameState: {
            ...state.gameState,
            isPaused: false,
            // Clear pausedPlayerIds so players don't still see "Resume"
            pausedPlayerIds: [],
            // Server shifts the turn start to account for the pause
            turnStartedAt:
              event.turnStartedAt !== undefined ? event.turnStartedAt : state.gameState.turnStartedAt,
          },
          // New end-game deadline (null = end countdown not active)
          endGameTimer: event.endsAt !== undefined ? event.endsAt : state.endGameTimer,
        };
      });
      break;

    case 'ERROR':
      if (event.code === 'REJOIN_FAILED') {
        failRejoin();
        break;
      }
      console.error('Server error:', event.message);
      store.addToast({ type: 'error', message: event.message });
      // Clear any pending loading states
      store.setCreatingRoom(false);
      store.setJoiningRoom(false);
      store.setStartingGame(false);
      store.setFlippingTile(false);
      resetClaimStatus();
      break;
  }
}

// ============================================================================
// Hook
// ============================================================================

export function useSocketConnection(): void {
  useEffect(() => {
    connectSocket();

    const cleanups = [
      onMessage(handleMessage),
      onConnect(handleConnected),
      onDisconnect(() => {
        const store = useGameStore.getState();
        store.setConnected(false);
        stopPing();
        // Need to rejoin after reconnecting; attempt counters are kept so the
        // retry limit is meaningful (they reset only on ROOM_REJOINED)
        clearRejoinTimer();
        hasRejoined = false;
        resetClaimStatus();
        store.setFlippingTile(false);
      }),
      onReconnectAttempt((attempt) => {
        const store = useGameStore.getState();
        store.setReconnecting(true);
        store.addToast({ type: 'info', message: `Reconnecting... (attempt ${attempt})`, duration: 2000 });
      }),
      onReconnect(() => {
        // Safety net in case the connect handler raced; rejoin is handled there
        const store = useGameStore.getState();
        store.setConnected(true);
        store.setReconnecting(false);
        store.addToast({ type: 'success', message: 'Reconnected!' });
      }),
      onReconnectFailed(() => {
        const store = useGameStore.getState();
        store.setReconnecting(false);
        store.addToast({ type: 'error', message: 'Failed to reconnect. Please refresh the page.' });
      }),
    ];

    // Socket may already be connected (StrictMode remount / HMR)
    if (isSocketConnected()) {
      useGameStore.getState().setConnected(true);
      startPing();
    }

    return () => {
      cleanups.forEach((fn) => fn());
      stopPing();
      // DON'T disconnect - the socket singleton persists for the app lifetime
    };
  }, []);
}
